import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Col, ControlLabel, Row } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { getOrgTermsContent } from '../../content/utils.js';
import { PROJECT_IMPACT, RATINGS } from '../../lib/schema.js';
import DatePickerAdapter from '../DatePickerAdapter.jsx';
import FieldGroup from '../FieldGroup.jsx';
import { FormError } from '../FormError.jsx';
import RadioOptionsField from '../RadioOptionsField.jsx';
import ProjectOwners from './ProjectFormFields/ProjectOwners.jsx';
import CheckboxAdapter from '../CheckboxAdapter.jsx';
import QuestionBlueIcon from '../icons/QuestionBlueIcon.jsx';
import CustomTooltip from '../CustomTooltip.jsx';
import MultiSelect from '../MultiSelect.jsx';
import { ProjectsLink } from '../links/index.jsx';

const scopeOptions = RATINGS.LARGE_MEDIUM_SMALL.map((rating) => ({
  label: capitalize(rating),
  value: rating,
}));

const projectImpactOptions = PROJECT_IMPACT.map((rating) => ({
  label: capitalize(rating),
  value: rating,
}));

const ProjectForm = (props) => {
  const {
    values,
    isArchived,
    editing,
    submitError,
    submitFailed,
    submitting,
    handleSubmit,
    projectOwners,
    projectStatuses,
    portfolios,
    selectedOwnerIds,
    updateSelectedOwnerIds,
    selectedPortfolio,
    updateSelectedPortfolio,
  } = props;

  const filteredByArchivedStatus = (arr) =>
    arr.filter((item) => !item.archived);
  const [projectStatusesActive, setProjectStatusesActive] = useState([]);

  useEffect(() => {
    if (projectStatuses) {
      setProjectStatusesActive(
        projectStatuses
          .map((s) => ({
            ...s,
            label: s.archived ? `${s.name} (archived)` : s.name,
            value: `${s.id}`,
          }))
          .filter(
            (item) =>
              !item.archived ||
              (values &&
                item.archived &&
                `${item.id}` === `${values.projectStatusId}`)
          )
      );
    }
  }, [values.projectStatusId, projectStatuses]);

  return (
    <form onSubmit={handleSubmit}>
      <Row>
        <Col xs={8}>
          <Field
            id="projectFormTitle"
            name="name"
            label="Title"
            placeholder={getOrgTermsContent('projectLabels.title')}
            type="text"
            component={FieldGroup}
            disabled={isArchived}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <Field
            id="projectFormOwner"
            name="projectOwner"
            label="projectFormOwner"
            placeholder="projectFormOwner"
            projectOwners={
              editing ? projectOwners : filteredByArchivedStatus(projectOwners)
            }
            selectedOwnerIds={selectedOwnerIds}
            updateSelectedOwnerIds={updateSelectedOwnerIds}
            component={ProjectOwners}
            disabled={isArchived}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <ControlLabel>Select {getOrgTermsContent('portfolio')}</ControlLabel>
          <MultiSelect
            multiple={false}
            options={portfolios || []}
            selectedOptions={selectedPortfolio}
            updateSelectedOptions={(val) => updateSelectedPortfolio(val)}
            placeholder={`Select ${getOrgTermsContent('portfolio')}`}
            subject={getOrgTermsContent('portfolio')}
            nameKey="name"
            additionalFields={{}}
            disabled={isArchived}
            error={null}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={8}>
          <Row>
            <Col sm={6}>
              <Field
                id="projectFormStartDate"
                name="startDate"
                label="Start Date"
                placeholderText="Select a date"
                component={DatePickerAdapter}
                allowNull
                disabled={isArchived}
              />
            </Col>
            <Col sm={6}>
              <Field
                id="projectFormEndDate"
                name="endDate"
                label="End Date"
                placeholderText="Select a date"
                component={DatePickerAdapter}
                allowNull
                disabled={isArchived}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <RadioOptionsField
            id="projectFormScope"
            label="Scope"
            name="scope"
            options={scopeOptions}
            disabled={isArchived}
          />
        </Col>
        <Col sm={6}>
          <RadioOptionsField
            id="projectFormImpact"
            label="Impact"
            name="impact"
            options={projectImpactOptions}
            disabled={isArchived}
          />
        </Col>
      </Row>

      <Row>
        <Col sm={6}>
          <RadioOptionsField
            id="projectStatusId"
            label="Status"
            name="projectStatusId"
            options={projectStatusesActive}
            disabled={isArchived}
          />
          {editing && (
            <div className="m-b-48">
              <ControlLabel>
                <div className="flex">
                  <span className="">
                    Archive {getOrgTermsContent('project')}
                  </span>
                  <CustomTooltip
                    placement="right"
                    tooltip={
                      <div style={{ maxWidth: '256px' }}>
                        <strong>
                          <h5
                            style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              lineHeight: '20px',
                              color: '#153142',
                            }}
                            className="m-b-8"
                          >
                            {getOrgTermsContent('project')} archive
                          </h5>
                        </strong>
                        <p
                          style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '20px',
                            color: '#7F8FA4',
                          }}
                          className="m-b-0 text-muted"
                        >
                          {getOrgTermsContent('archivedProjectDescription')}
                        </p>
                      </div>
                    }
                    id="tooltip"
                  >
                    <Button
                      aria-label="View Support for Change modal"
                      type="button"
                      className="flex flex-align-center m-l-8 btn-last-update-icon"
                    >
                      <QuestionBlueIcon />
                    </Button>
                  </CustomTooltip>
                </div>
              </ControlLabel>
              <Field
                name="archived"
                label="Archived"
                type="checkbox"
                component={CheckboxAdapter}
                defaultValue={isArchived}
              />
            </div>
          )}
        </Col>
      </Row>

      <Row className="m-b-48">
        {submitFailed && submitError ? (
          <FormError submitError={submitError} />
        ) : null}
        <Col xs={12}>
          <div className="flex">
            <div>
              <ProjectsLink>
                <Button bsStyle="default">Cancel</Button>
              </ProjectsLink>
              <Button
                className="m-l-12"
                bsStyle="primary"
                disabled={submitting}
                type="submit"
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </form>
  );
};

ProjectForm.propTypes = {
  editing: PropTypes.bool.isRequired,
};

export default ProjectForm;
